import { useState } from "react";
import { Link } from "wouter";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { 
  Camera, 
  Play, 
  ExternalLink,
  ArrowLeft,
  Heart,
  Calendar,
  MapPin,
  Users,
  Download
} from "lucide-react";
import laMirageLogo from "@assets/download.png";

const galleryItems = [
  {
    id: 1,
    type: "image",
    title: "Traditional Kerala Wedding Ceremony",
    url: "https://images.unsplash.com/photo-1583939003579-730e3918a45a?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80",
    category: "ceremony",
    description: "Beautiful traditional Kerala wedding ceremony at LA Mirage"
  },
  {
    id: 2,
    type: "image", 
    title: "Grand Reception Hall",
    url: "https://images.unsplash.com/photo-1519741497674-611481863552?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80",
    category: "venue",
    description: "Our elegant reception hall decorated for wedding celebrations"
  },
  {
    id: 3,
    type: "video",
    title: "Wedding Highlight Reel",
    url: "https://player.vimeo.com/video/123456789",
    thumbnail: "https://images.unsplash.com/photo-1606800052052-a08af7148866?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80",
    category: "highlights",
    description: "A beautiful highlight reel of recent weddings at LA Mirage"
  },
  {
    id: 4,
    type: "image",
    title: "Bridal Photography",
    url: "https://images.unsplash.com/photo-1511285560929-80b456fea0bc?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80",
    category: "photography",
    description: "Professional bridal photography sessions"
  },
  {
    id: 5,
    type: "image",
    title: "Wedding Decorations",
    url: "https://images.unsplash.com/photo-1465495976277-4387d4b0e4a6?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80",
    category: "decoration",
    description: "Stunning floral and lighting decorations"
  },
  {
    id: 6,
    type: "image",
    title: "Catering & Cuisine",
    url: "https://images.unsplash.com/photo-1520854221256-17451cc331bf?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80",
    category: "catering",
    description: "Exquisite Kerala and multi-cuisine wedding feast"
  }
];

const categories = [
  { id: "all", label: "All", count: galleryItems.length },
  { id: "ceremony", label: "Ceremony", count: galleryItems.filter(item => item.category === "ceremony").length },
  { id: "venue", label: "Venue", count: galleryItems.filter(item => item.category === "venue").length },
  { id: "photography", label: "Photography", count: galleryItems.filter(item => item.category === "photography").length },
  { id: "decoration", label: "Decoration", count: galleryItems.filter(item => item.category === "decoration").length },
  { id: "catering", label: "Catering", count: galleryItems.filter(item => item.category === "catering").length }
];

export default function Gallery() {
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [selectedItem, setSelectedItem] = useState<any>(null);

  const filteredItems = selectedCategory === "all" 
    ? galleryItems 
    : galleryItems.filter(item => item.category === selectedCategory);

  return (
    <div className="min-h-screen bg-gradient-to-br from-black via-gray-900 to-black">
      {/* Header */}
      <header className="border-b border-dark-yellow/20 bg-black/50 backdrop-blur-sm sticky top-0 z-50">
        <div className="container mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <img 
              src={laMirageLogo} 
              alt="LA Mirage Logo" 
              className="w-10 h-10 object-contain"
            />
            <div>
              <h1 className="text-xl font-bold text-white">LA Mirage Koratty</h1>
              <p className="text-xs text-dark-yellow">Premium Wedding Venue</p>
            </div>
          </div>
          <div className="flex items-center space-x-4">
            <Link href="/">
              <Button variant="outline" className="border-dark-yellow/30 text-dark-yellow hover:bg-dark-yellow/10">
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back to Home
              </Button>
            </Link>
            <Link href="/login">
              <Button className="bg-dark-yellow text-black hover:bg-dark-yellow/90">
                Login
              </Button>
            </Link>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="py-20">
        <div className="container mx-auto px-4 text-center">
          <Badge className="bg-dark-yellow/20 text-dark-yellow border-dark-yellow/30 mb-6">
            <Camera className="w-4 h-4 mr-2" />
            Wedding Gallery
          </Badge>
          <h1 className="text-5xl lg:text-6xl font-playfair font-bold text-white mb-6">
            Captured Moments
          </h1>
          <p className="text-xl text-gray-300 max-w-3xl mx-auto mb-8">
            Explore our collection of beautiful wedding moments, venue highlights, and ceremonial celebrations 
            that showcase the magic of LA Mirage Koratty.
          </p>
          
          {/* Stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 max-w-2xl mx-auto mb-12">
            <div className="text-center">
              <div className="text-3xl font-bold text-dark-yellow mb-2">500+</div>
              <div className="text-gray-400 text-sm">Weddings Captured</div>
            </div>
            <div className="text-center">
              <div className="text-3xl font-bold text-dark-yellow mb-2">10K+</div>
              <div className="text-gray-400 text-sm">Photos Taken</div>
            </div>
            <div className="text-center">
              <div className="text-3xl font-bold text-dark-yellow mb-2">50+</div>
              <div className="text-gray-400 text-sm">Video Highlights</div>
            </div>
            <div className="text-center">
              <div className="text-3xl font-bold text-dark-yellow mb-2">100%</div>
              <div className="text-gray-400 text-sm">Happy Couples</div>
            </div>
          </div>
        </div>
      </section>

      {/* Category Filter */}
      <section className="py-8 bg-black/30">
        <div className="container mx-auto px-4">
          <div className="flex flex-wrap justify-center gap-4">
            {categories.map((category) => (
              <button
                key={category.id}
                onClick={() => setSelectedCategory(category.id)}
                className={`px-6 py-3 rounded-full text-sm font-medium transition-all ${
                  selectedCategory === category.id
                    ? 'bg-dark-yellow text-black'
                    : 'bg-white/10 text-white hover:bg-white/20'
                }`}
              >
                {category.label} ({category.count})
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Gallery Grid */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {filteredItems.map((item) => (
              <Card 
                key={item.id} 
                className="bg-white/5 border-dark-yellow/20 overflow-hidden hover:bg-white/10 transition-all duration-300 group cursor-pointer"
                onClick={() => setSelectedItem(item)}
              >
                <div className="relative aspect-[4/3] overflow-hidden">
                  <img
                    src={item.type === 'video' ? item.thumbnail : item.url}
                    alt={item.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                  />
                  {item.type === 'video' && (
                    <div className="absolute inset-0 flex items-center justify-center bg-black/30">
                      <div className="w-16 h-16 bg-dark-yellow rounded-full flex items-center justify-center">
                        <Play className="w-6 h-6 text-black ml-1" />
                      </div>
                    </div>
                  )}
                  <div className="absolute top-4 right-4">
                    <Badge className="bg-dark-yellow/20 text-dark-yellow border-dark-yellow/30">
                      {item.category}
                    </Badge>
                  </div>
                </div>
                <CardContent className="p-6">
                  <h3 className="text-lg font-semibold text-white mb-2">{item.title}</h3>
                  <p className="text-gray-400 text-sm">{item.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Contact CTA */}
      <section className="py-20 bg-gradient-to-r from-dark-yellow/10 to-amber-50/10">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-4xl font-playfair font-bold text-white mb-6">
            Ready to Create Your Own Memories?
          </h2>
          <p className="text-xl text-gray-300 mb-8 max-w-2xl mx-auto">
            Let us capture the most important moments of your special day with our professional photography and videography services.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link href="/login">
              <Button size="lg" className="bg-dark-yellow text-black hover:bg-dark-yellow/90 text-lg px-8">
                Plan Your Wedding
                <Calendar className="w-5 h-5 ml-2" />
              </Button>
            </Link>
            <Button size="lg" variant="outline" className="border-dark-yellow text-dark-yellow hover:bg-dark-yellow/10 text-lg px-8">
              <ExternalLink className="w-5 h-5 mr-2" />
              Contact Us
            </Button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-dark-yellow/20 bg-black/50 py-12">
        <div className="container mx-auto px-4">
          <div className="grid md:grid-cols-4 gap-8">
            <div className="col-span-2">
              <div className="flex items-center space-x-3 mb-4">
                <img 
                  src={laMirageLogo} 
                  alt="LA Mirage Logo" 
                  className="w-8 h-8 object-contain"
                />
                <h3 className="text-lg font-bold text-white">LA Mirage Koratty</h3>
              </div>
              <p className="text-gray-400 mb-4">
                Creating magical wedding moments in the heart of Kerala. 
                Your dream wedding destination with world-class facilities and service.
              </p>
              <div className="flex space-x-4">
                <Heart className="w-5 h-5 text-dark-yellow" />
                <span className="text-gray-400 text-sm">500+ Happy Couples</span>
              </div>
            </div>
            
            <div>
              <h4 className="text-white font-semibold mb-4">Contact Info</h4>
              <div className="space-y-3 text-gray-400 text-sm">
                <div className="flex items-center space-x-2">
                  <MapPin className="w-4 h-4 text-dark-yellow" />
                  <span>Koratty, Thrissur District, Kerala</span>
                </div>
                <div className="flex items-center space-x-2">
                  <Users className="w-4 h-4 text-dark-yellow" />
                  <span>+91 9446 77 6666</span>
                </div>
              </div>
            </div>
            
            <div>
              <h4 className="text-white font-semibold mb-4">Quick Links</h4>
              <div className="space-y-2">
                <Link href="/" className="block text-gray-400 hover:text-dark-yellow transition-colors text-sm">
                  Home
                </Link>
                <Link href="/login" className="block text-gray-400 hover:text-dark-yellow transition-colors text-sm">
                  Login
                </Link>
                <a 
                  href="https://la-mirage.in/booking/" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="block text-gray-400 hover:text-dark-yellow transition-colors text-sm"
                >
                  Book Online
                </a>
              </div>
            </div>
          </div>
          
          <div className="border-t border-dark-yellow/20 mt-8 pt-8 text-center text-gray-400 text-sm">
            <p>&copy; 2025 LA Mirage Koratty. All rights reserved.</p>
          </div>
        </div>
      </footer>

      {/* Modal for enlarged view */}
      {selectedItem && (
        <div 
          className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center p-4"
          onClick={() => setSelectedItem(null)}
        >
          <div className="relative max-w-4xl w-full">
            <button
              onClick={() => setSelectedItem(null)}
              className="absolute -top-12 right-0 text-white hover:text-dark-yellow transition-colors"
            >
              ✕ Close
            </button>
            {selectedItem.type === 'video' ? (
              <div className="aspect-video">
                <iframe
                  src={selectedItem.url}
                  className="w-full h-full rounded-lg"
                  frameBorder="0"
                  allow="autoplay; fullscreen; picture-in-picture"
                />
              </div>
            ) : (
              <img
                src={selectedItem.url}
                alt={selectedItem.title}
                className="w-full h-auto rounded-lg"
              />
            )}
            <div className="mt-4 text-center">
              <h3 className="text-xl font-semibold text-white mb-2">{selectedItem.title}</h3>
              <p className="text-gray-300">{selectedItem.description}</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}